import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Button from "../components/button";
import ThemeToggle from "../components/theme-toggle";
import { ReactComponent as AppleLogo } from "../static/apple-logo-svgrepo-com.svg";
import appIcon from "../static/icon.png";
import devicePreview from "../static/website.png";
import playlistsScreenshot360 from "../static/iptv/web/iptv-appstore-01-360.jpg";
import playlistsScreenshot720 from "../static/iptv/web/iptv-appstore-01-720.jpg";
import playerScreenshot360 from "../static/iptv/web/iptv-appstore-02-360.jpg";
import playerScreenshot720 from "../static/iptv/web/iptv-appstore-02-720.jpg";
import themesScreenshot360 from "../static/iptv/web/iptv-appstore-03-360.jpg";
import themesScreenshot720 from "../static/iptv/web/iptv-appstore-03-720.jpg";
import syncScreenshot360 from "../static/iptv/web/iptv-appstore-04-360.jpg";
import syncScreenshot720 from "../static/iptv/web/iptv-appstore-04-720.jpg";
import remindersScreenshot360 from "../static/iptv/web/iptv-appstore-05-360.jpg";
import remindersScreenshot720 from "../static/iptv/web/iptv-appstore-05-720.jpg";
import streamQualityScreenshot360 from "../static/iptv/web/iptv-stream-quality-360.jpg";
import streamQualityScreenshot720 from "../static/iptv/web/iptv-stream-quality-720.jpg";
import vlcScreenshot360 from "../static/iptv/web/iptv-vlc-360.jpg";
import vlcScreenshot720 from "../static/iptv/web/iptv-vlc-720.jpg";
import { IOS_URL, SUPPORT_EMAIL } from "../constants/data";

const createResponsiveImage = (image360, image720) => ({
  src: image720,
  srcSet: `${image360} 360w, ${image720} 720w`,
});

const features = [
  {
    label: "Playlists",
    title: "M3U, M3U8, and HLS ready",
    description:
      "Import IPTV playlists quickly and browse channels with schedule information in one clean list.",
    image: createResponsiveImage(playlistsScreenshot360, playlistsScreenshot720),
    imageAlt: "IPTV Time playlist view showing multiple channels and schedule information.",
  },
  {
    label: "Player",
    title: "Native playback with program context",
    description:
      "Open a channel in the built-in player and keep the current and upcoming programming visible while you watch.",
    image: createResponsiveImage(playerScreenshot360, playerScreenshot720),
    imageAlt: "Native video player with current and upcoming program information.",
  },
  {
    label: "Reminders",
    title: "Alerts for favourite programs",
    description:
      "Set reminders from the program view so important matches, films, and live events do not slip by.",
    image: createResponsiveImage(remindersScreenshot360, remindersScreenshot720),
    imageAlt: "Reminder menu for an IPTV program with notification actions.",
  },
  {
    label: "Sync",
    title: "iCloud sync across devices",
    description:
      "Keep playlists and channel organization available on your Apple devices without rebuilding the same setup twice.",
    image: createResponsiveImage(syncScreenshot360, syncScreenshot720),
    imageAlt: "Playlist screen highlighting iCloud sync across devices.",
  },
  {
    label: "Quality",
    title: "Choose the stream quality you need",
    description:
      "Switch stream variants when your connection changes so you can prioritize stability or sharper playback without leaving the player.",
    image: createResponsiveImage(
      streamQualityScreenshot360,
      streamQualityScreenshot720
    ),
    imageAlt: "IPTV Time player showing stream quality options for a channel.",
  },
  {
    label: "Themes",
    title: "Dark and light appearances",
    description:
      "Switch between bright and dark looks depending on the room, the time of day, or just your preference.",
    image: createResponsiveImage(themesScreenshot360, themesScreenshot720),
    imageAlt: "Light and dark theme comparison for IPTV Time on iPhone.",
  },
  {
    label: "VLC",
    title: "Open channels in VLC",
    description:
      "Hand off playback to VLC when you want an external player while keeping playlists and channel discovery inside IPTV Time.",
    image: createResponsiveImage(vlcScreenshot360, vlcScreenshot720),
    imageAlt: "IPTV Time action sheet with an option to open a channel in VLC.",
  },
];

export default function Home({ isDarkTheme, onToggleTheme }) {
  return (
    <Page>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <Shell>
        <TopBar>
          <Brand>
            <BrandMark src={appIcon} alt="" aria-hidden="true" />
            <BrandText>
              <strong>IPTV Time</strong>
              <span>Playlist-based IPTV player for Apple devices</span>
            </BrandText>
          </Brand>
          <Nav aria-label="Primary">
            <NavLink href={`mailto:${SUPPORT_EMAIL}?subject=IPTV%20Time%20Support`}>
              Contact
            </NavLink>
            <StyledLink to="/privacy-policy">Privacy Policy</StyledLink>
            <ThemeToggle checked={isDarkTheme} onClick={onToggleTheme} />
          </Nav>
        </TopBar>

        <Hero id="main-content">
          <HeroCopy>
            <Headline>
              Organize playlists, live channels, reminders, and TV guide in one
              IPTV app.
            </Headline>
            <Lead>
              IPTV Time brings together M3U and M3U8 playlists, HLS playback,
              native video performance, reminder actions, iCloud sync, iPad
              layouts, and dark or light themes in the same focused experience.
            </Lead>
            <ActionRow>
              <Button href={IOS_URL}>
                <AppStoreLabel>
                  <span>Download on the App Store</span>
                  <AppStoreIcon aria-hidden="true" focusable="false" />
                </AppStoreLabel>
              </Button>
            </ActionRow>
          </HeroCopy>

          <HeroVisual>
            <PreviewCard>
              <PreviewGlow />
              <PreviewTitle>Built for iPhone and iPad</PreviewTitle>
              <PreviewText>
                Keep channels, bookmarks, reminders, and playlists in sync while
                using a layout that feels native on both compact and larger
                screens.
              </PreviewText>
              <PreviewFrame>
                <PreviewImage
                  src={devicePreview}
                  alt="Preview of IPTV Time on iPhone and iPad."
                />
              </PreviewFrame>
            </PreviewCard>
          </HeroVisual>
        </Hero>

        <Section>
          <FeatureGrid aria-label="IPTV Time features">
            {features.map((feature) => (
              <FeatureCard key={feature.title}>
                <FeatureFigure>
                  <FeatureImage
                    src={feature.image.src}
                    srcSet={feature.image.srcSet}
                    sizes="(max-width: 640px) calc(100vw - 68px), (max-width: 960px) calc(50vw - 44px), 280px"
                    loading="lazy"
                    decoding="async"
                    alt={feature.imageAlt}
                  />
                </FeatureFigure>
                <FeatureBody>
                  <FeatureLabel>{feature.label}</FeatureLabel>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureBody>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <BottomPanel>
          <BottomCopy>
            <SupportEyebrow>Support</SupportEyebrow>
            <BottomTitle>Need help with playlists or app setup?</BottomTitle>
            <BottomText>
              Reach out if you need assistance importing lists, understanding
              playback behavior, or reporting a problem.
            </BottomText>
          </BottomCopy>
          <BottomActions>
            <Button
              href={`mailto:${SUPPORT_EMAIL}?subject=IPTV%20Time%20Support`}
              variant="secondary"
              target="_self"
              rel=""
            >
              Email us
            </Button>
          </BottomActions>
        </BottomPanel>
      </Shell>
    </Page>
  );
}

const Page = styled.main`
  padding: 32px 20px 60px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    padding: 20px 16px 48px;
  }
`;

const SkipLink = styled.a`
  position: absolute;
  left: 20px;
  top: 12px;
  z-index: 10;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.navy};
  color: #fff;
  transform: translateY(-140%);
  transition: transform 0.2s ease;

  &:focus {
    transform: translateY(0);
  }
`;

const Shell = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 28px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const BrandMark = styled.img`
  width: 68px;
  height: 68px;
  border-radius: 20px;
  box-shadow: 0 16px 30px rgba(22, 52, 64, 0.18);
  object-fit: cover;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 1.6rem;
    line-height: 1.05;
  }

  span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 1.15rem;
    line-height: 1.3;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 18px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1rem;
`;

const NavLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.navSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.cardSurface};
    border-color: ${({ theme }) => theme.colors.accentDark};
  }

  &:focus-visible {
    transform: translateY(-1px);
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.navSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.cardSurface};
    border-color: ${({ theme }) => theme.colors.accentDark};
  }

  &:focus-visible {
    transform: translateY(-1px);
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 36px;
  align-items: center;
  padding: 28px 0 68px;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    grid-template-columns: 1fr;
    padding: 12px 0 52px;
  }
`;

const HeroCopy = styled.div`
  max-width: 620px;
`;

const Headline = styled.h1`
  margin: 0 0 18px;
  font-size: clamp(2.3rem, 4.6vw, 4.2rem);
  line-height: 1.02;
  letter-spacing: -0.05em;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    font-size: clamp(2rem, 8vw, 3rem);
  }
`;

const Lead = styled.p`
  margin: 0;
  max-width: 560px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 1.08rem;
  line-height: 1.8;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
`;

const AppStoreLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
`;

const AppStoreIcon = styled(AppleLogo)`
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 auto;
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
`;

const PreviewCard = styled.div`
  position: relative;
  width: min(100%, 500px);
  padding: 28px;
  border-radius: 32px;
  background: ${({ theme }) => theme.colors.cardSurface};
  border: 1px solid ${({ theme }) => theme.colors.cardBorderStrong};
  box-shadow: ${({ theme }) => theme.colors.shadow};
  backdrop-filter: blur(14px);
  overflow: hidden;
`;

const PreviewGlow = styled.div`
  position: absolute;
  inset: auto -90px -120px auto;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(45, 167, 183, 0.24);
  filter: blur(10px);
`;

const PreviewTitle = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-size: 2rem;
  line-height: 1.05;
`;

const PreviewText = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.muted};
`;

const PreviewFrame = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.previewFrameStart} 0%,
    ${({ theme }) => theme.colors.previewFrameEnd} 100%
  );
  border: 1px solid ${({ theme }) => theme.colors.previewFrameBorder};
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  filter: drop-shadow(0 16px 28px rgba(31, 41, 55, 0.12));
`;

const Section = styled.section`
  padding: 12px 0 32px;
`;

const FeatureGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.li`
  display: flex;
  flex-direction: column;
  border-radius: 26px;
  background: ${({ theme }) => theme.colors.cardSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  overflow: hidden;
`;

const FeatureFigure = styled.div`
  margin: 0;
  padding: 18px 18px 0;
`;

const FeatureImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 3 / 5;
  object-fit: cover;
  object-position: top;
  border-radius: 20px;
  border: 1px solid rgba(23, 49, 60, 0.1);
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const FeatureBody = styled.div`
  padding: 22px 26px 26px;
`;

const FeatureLabel = styled.div`
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.accentDark};
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.3rem;
  line-height: 1.15;
`;

const FeatureDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.98rem;
  line-height: 1.75;
`;

const BottomPanel = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 36px;
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.bottomPanelStart} 0%,
    ${({ theme }) => theme.colors.bottomPanelEnd} 100%
  );
  color: #fff;
  box-shadow: ${({ theme }) => theme.colors.bottomPanelShadow};

  @media only screen and (max-width: ${({ theme }) => theme.mobileBreakpoint}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BottomCopy = styled.div`
  max-width: 580px;
`;

const SupportEyebrow = styled.div`
  color: rgba(255, 255, 255, 0.96);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const BottomTitle = styled.h2`
  margin: 12px 0 10px;
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  line-height: 1.05;
  letter-spacing: -0.04em;
`;

const BottomText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.96);
`;

const BottomActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;
